import { BookOutlined, Phone, Star, StarBorder } from '@mui/icons-material';
import CallEndIcon from '@mui/icons-material/CallEnd';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import PauseIcon from '@mui/icons-material/Pause';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Rating,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify } from 'react-admin';
import { CallStatus } from '../../../../contexts/PhoneCallsContext/phone-calls.types';
import { usePhoneCalls } from '../../../../contexts/PhoneCallsContext/PhoneCallsContext';
import useDebounce from '../../../../hooks/useDebounce';
import {
  AgentDirectoryEntry,
  CallDirection,
  CallPurpose,
  SimplifiedMember,
} from '../../../../interfaces/calls';
import coreService from '../../../../services/core';
import { getCapitalizedWord } from '../../../../util/stringFormat';
import { DialPad } from '../Dialpad/Dialpad';
import { Directory } from '../Directory/Directory';

interface OutgoingCallModalProps {
  open: boolean;
}

export const OutgoingCallModal: FC<OutgoingCallModalProps> = ({ open }, ref) => {
  const [isSearchingMembers, setIsSearchingMembers] = useState<boolean>(false);
  const [members, setMembers] = useState<SimplifiedMember[]>([]);
  const notify = useNotify();

  const {
    agent,
    activeCallState,
    callDuration,
    isMuted,
    isOnHold,
    callLog,
    conference,
    selectedMember,
    memberPhoneNumber,
    isAwaitingRating,
    audioRating,
    callNotes,
    agentDirectory,
    agentDirectoryLastUpdatedAt,
    canInitiateTransfer,
    isDirectoryOpen,
    callPurpose,
    setCallPurpose,
    setAudioRating,
    setCallNotes,
    saveCallSummary,
    sendDigitsOnCall,
    setMemberPhoneNumber,
    startOutgoingCall,
    hangUpCall,
    muteCall,
    holdUnholdConference,
    closeOutgoingCallModal,
    setSelectedMember,
    setIsDirectoryOpen,
    refreshAgentDirectory,
    initiateWarmTransfer,
    initiateBlindTransfer,
  } = usePhoneCalls();

  const formatCallDuration = useMemo(() => {
    const minutes = Math.floor(callDuration / 60);
    const seconds = callDuration % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, [callDuration]);

  const searchUsersByTerm = useCallback(
    async ({
      searchTerm,
      target,
    }: {
      searchTerm?: string | null;
      target: 'providers' | 'members';
    }) => {
      if (!searchTerm) {
        return [];
      }
      const { data } = await coreService.fetchMembers({ searchTerm, target });
      return data.map((member) => ({
        id: member.id,
        phone: member.phone,
        name: member.fullName,
      }));
    },
    [],
  );

  const debouncedFetchMembers = useDebounce(
    useCallback(
      async (param: { searchTerm: string }) => {
        setIsSearchingMembers(true);
        setMembers(await searchUsersByTerm({ ...param, target: 'members' }));
        setIsSearchingMembers(false);
      },
      [searchUsersByTerm],
    ),
  );

  const onMemberChange = (member: SimplifiedMember | null) => {
    setSelectedMember(member);

    if (member && !isAwaitingRating && activeCallState.status === CallStatus.Idle) {
      setMemberPhoneNumber(member.phone.replaceAll('+1', '') || '');
    }
  };

  // Reset modal state for new calls
  const resetModalState = useCallback(() => {
    setIsSearchingMembers(false);
    setMembers([]);
  }, []);

  // Reset state when modal opens for a new call (when no active call)
  useEffect(() => {
    if (open && !activeCallState.call) {
      resetModalState();
    }
  }, [open, activeCallState.call, resetModalState]);

  const handleStartCall = useCallback(async () => {
    if (!memberPhoneNumber || memberPhoneNumber.length < 10 || !agent) {
      return;
    }

    try {
      await startOutgoingCall(memberPhoneNumber);
    } catch (error) {
      console.error('Error starting call:', error);
      notify('Error starting call', 'error');
    }
  }, [memberPhoneNumber, notify, startOutgoingCall, agent]);

  const handleEndCall = async () => {
    try {
      await hangUpCall();
    } catch (error) {
      console.error('Error ending call:', error);
      notify('Error ending call, but call has been disconnected locally', 'warning');
    }
  };

  const handleSaveAndClose = async () => {
    await saveCallSummary();
    resetModalState();
    closeOutgoingCallModal();
  };

  const handleToggleMute = async () => {
    try {
      muteCall(!isMuted);
    } catch (error) {
      console.error('Error toggling mute:', error);
      notify('Failed to toggle mute', 'error');
    }
  };

  const handleToggleHold = async () => {
    try {
      if (!memberPhoneNumber) {
        notify('No participant phone number available', 'error');
        return;
      }

      await holdUnholdConference(!isOnHold, `+1${memberPhoneNumber}`);
    } catch (error) {
      console.error('Error toggling hold:', error);
      notify('Failed to toggle hold', 'error');
    }
  };

  const handleOpenDirectory = useCallback(async () => {
    await refreshAgentDirectory();
    setIsDirectoryOpen(true);
  }, [refreshAgentDirectory, setIsDirectoryOpen]);

  const handleCloseDirectory = useCallback(() => {
    setIsDirectoryOpen(false);
  }, [setIsDirectoryOpen]);

  const handleWarmTransfer = useCallback(
    async (targetAgent: AgentDirectoryEntry) => {
      try {
        await initiateWarmTransfer(targetAgent);
      } catch (error) {
        console.error('Error initiating warm transfer:', error);
        notify('Failed to initiate warm transfer', 'error');
      }
    },
    [initiateWarmTransfer, notify],
  );

  const handleBlindTransfer = useCallback(
    async (targetAgent: AgentDirectoryEntry) => {
      try {
        await initiateBlindTransfer(targetAgent);
      } catch (error) {
        console.error('Error initiating blind transfer:', error);
        notify('Failed to initiate blind transfer', 'error');
      }
    },
    [initiateBlindTransfer, notify],
  );

  const handleDialPadPress = useCallback(
    (digit: string) => {
      if (memberPhoneNumber.length < 10) {
        setMemberPhoneNumber(memberPhoneNumber + digit);
      }

      if (activeCallState.call) {
        sendDigitsOnCall(digit);
      }
    },
    [activeCallState.call, memberPhoneNumber, sendDigitsOnCall, setMemberPhoneNumber],
  );

  const isStartCallDisabled =
    !agent || !callPurpose || !memberPhoneNumber || memberPhoneNumber.length < 10;

  // Keyboard input handler for DialPad
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Only capture keyboard input when DialPad is visible
      if (activeCallState.call || selectedMember) {
        return;
      }

      const key = event.key;

      // Handle numeric keys 0-9 and special characters
      if ((key >= '0' && key <= '9') || key === '*' || key === '#') {
        event.preventDefault();
        handleDialPadPress(key);
      }

      // Handle backspace to delete last digit
      if (
        key === 'Backspace' &&
        memberPhoneNumber.length > 0 &&
        !isAwaitingRating &&
        activeCallState.status === CallStatus.Idle
      ) {
        event.preventDefault();
        setMemberPhoneNumber(memberPhoneNumber.slice(0, -1));
      }

      // Handle Enter to start call if number is valid
      if (key === 'Enter' && memberPhoneNumber.length === 10 && !isStartCallDisabled) {
        event.preventDefault();
        handleStartCall();
      }
    };

    // Add event listener when modal is open and DialPad is visible
    if (open && !activeCallState.call && !selectedMember) {
      window.addEventListener('keydown', handleKeyPress);
    }

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [
    open,
    activeCallState.call,
    selectedMember,
    memberPhoneNumber,
    isStartCallDisabled,
    handleDialPadPress,
    handleStartCall,
    isAwaitingRating,
    activeCallState.status,
    setMemberPhoneNumber,
  ]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleSaveAndClose}
        maxWidth={isDirectoryOpen ? 'md' : 'sm'}
        fullWidth
        sx={{
          '& .MuiDialog-container': {
            overflow: 'visible',
          },
          '& .MuiDialog-container > .MuiPaper-elevation': {
            borderRadius: '12px',
            height: '90vh',
            maxHeight: '800px',
            minHeight: '600px',
            display: 'flex',
            flexDirection: 'row',
            width: isDirectoryOpen ? '920px' : undefined,
            position: 'relative',
            overflow: 'visible',
          },
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            borderRadius: '12px',
            overflow: 'hidden',
          }}>
          <DialogTitle
            sx={{
              backgroundColor: '#F5F5F5',
              paddingTop: '8px',
              paddingBottom: '8px',
              paddingLeft: '1rem',
              paddingRight: '1rem',
            }}>
            <Box display='flex' justifyContent='space-between' alignItems='center' width='100%'>
              <Autocomplete
                className='member-selector'
                options={members}
                filterOptions={(x) => x}
                onChange={(_, v) => onMemberChange(v)}
                noOptionsText='No members found'
                value={selectedMember}
                disabled={!!activeCallState.call}
                sx={{
                  height: '30px',
                  borderRadius: '10px',
                  '&.member-selector .MuiInputBase-fullWidth.MuiOutlinedInput-root.MuiInputBase-root':
                    {
                      borderRadius: '10px',
                    },
                }}
                getOptionLabel={(option) => `${option.name} ID:${option.id}`}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                    placeholder='Search a member'
                    disabled={!!activeCallState.call}
                    onChange={(e) =>
                      !activeCallState.call && debouncedFetchMembers({ searchTerm: e.target.value })
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <Box sx={{ marginRight: '33px', marginTop: '4px' }}>
                          {isSearchingMembers && <CircularProgress color='inherit' size={14} />}
                          {params.InputProps.endAdornment}
                        </Box>
                      ),
                      startAdornment: (
                        <>
                          <Avatar sx={{ color: 'action.active', mr: 1 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      sx: {
                        '&& input.MuiAutocomplete-input': {
                          padding: '0 4px 0 6px',
                          width: '200px',
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component='li' {...props}>
                    <Avatar sx={{ mr: 2, bgcolor: '#007bff' }}>
                      {`${option.name?.[0]}${option.name?.[1]}`.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography fontSize='0.8rem'>{option.name}</Typography>
                      <Typography variant='caption' color='textSecondary'>
                        ID:{option.id} Phone:{option.phone}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />

              <IconButton
                onClick={handleSaveAndClose}
                sx={{
                  width: '32px',
                  height: '32px',
                  padding: '4px',
                  marginRight: '8px',
                }}>
                <CloseIcon sx={{ width: '20px', height: '20px' }} />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{
              padding: 0,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
            <Box
              display='flex'
              gap={2}
              pl={3}
              pr={3}
              pb='20px'
              pt={3}
              borderBottom='1px solid #CDCDCD'
              alignItems='center'
              justifyContent='center'
              height='15%'>
              <Box flex={1} display='flex' flexDirection='column'>
                <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
                  Phone Number
                </Typography>
                <TextField
                  disabled={
                    isAwaitingRating ||
                    activeCallState.status === CallStatus.Ended ||
                    activeCallState.status === CallStatus.Ending
                  }
                  variant='outlined'
                  placeholder='5551234567'
                  size='small'
                  InputLabelProps={{ shrink: true }}
                  onChange={(v) => {
                    let phoneNumber = v.target.value.replace(/\D/g, '');

                    if (phoneNumber.startsWith('1') && phoneNumber.length === 11) {
                      phoneNumber = phoneNumber.slice(1);
                    }

                    if (phoneNumber.length <= 10) {
                      setMemberPhoneNumber(phoneNumber);
                    }
                  }}
                  value={memberPhoneNumber}
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ display: 'flex', alignItems: 'center', pr: '4px' }}>
                        <Typography variant='body2' color='textSecondary' sx={{ mr: 0.5 }}>
                          +1
                        </Typography>
                      </Box>
                    ),
                  }}
                />
              </Box>

              <Box flex={1} display='flex' flexDirection='column'>
                <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
                  Call Purpose
                </Typography>
                <Autocomplete
                  disabled={
                    isAwaitingRating ||
                    activeCallState.status === CallStatus.Ended ||
                    activeCallState.status === CallStatus.Ending
                  }
                  fullWidth
                  options={Object.values(CallPurpose)}
                  getOptionLabel={(option) => getCapitalizedWord(option.split('_').join(' '))}
                  value={callPurpose}
                  onChange={(_, value) => setCallPurpose(value || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant='outlined'
                      placeholder='Select call purpose'
                      size='small'
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  disableClearable
                />
              </Box>
            </Box>

            <Box display='flex' flexDirection='column' width='100%' sx={{ overflowY: 'scroll' }}>
              {/* Status Message or Dial Pad */}
              {(!isAwaitingRating ||
                (activeCallState.status !== CallStatus.Ended &&
                  activeCallState.status !== CallStatus.Ending)) && (
                <Box pt={2}>
                  <DialPad onNumberPress={handleDialPadPress} />
                </Box>
              )}

              {/* Notes Section - Show during call and after call */}
              {(activeCallState.call || (!activeCallState.call && callLog)) && (
                <Box display='flex' flexDirection='column' flex={1} p={3}>
                  <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                    <Typography variant='subtitle1' alignSelf='flex-start'>
                      Notes
                    </Typography>
                  </Box>

                  {/* Notes Input */}
                  <TextField
                    disabled={!callLog}
                    value={callNotes}
                    onChange={(e) => setCallNotes(e.target.value)}
                    placeholder='Enter call notes...'
                    variant='outlined'
                    multiline
                    rows={3}
                    fullWidth
                  />

                  {/* Rating Section - Show after call ends */}
                  {!activeCallState.call && callLog && isAwaitingRating && (
                    <Box
                      display='flex'
                      flexDirection='column'
                      alignItems='center'
                      justifyContent='center'
                      mt={6}
                      flexShrink={0}>
                      <Typography variant='subtitle2' mb={0.5} textAlign='center'>
                        How was the audio?
                      </Typography>
                      <Box display='flex' alignItems='center' mb={0.5}>
                        <Rating
                          name='audio-rating'
                          value={audioRating}
                          onChange={(_, newValue) => setAudioRating(newValue)}
                          max={5}
                          size='medium'
                          icon={<Star fontSize='inherit' sx={{ color: '#FFC107' }} />}
                          emptyIcon={<StarBorder fontSize='inherit' sx={{ color: '#FFC107' }} />}
                        />
                      </Box>
                      <Box
                        display='flex'
                        justifyContent='space-between'
                        width='100%'
                        maxWidth='180px'
                        mb={1}>
                        <Typography variant='caption' color='textSecondary' fontSize='11px'>
                          Very bad
                        </Typography>
                        <Typography variant='caption' color='textSecondary' fontSize='11px'>
                          Very good
                        </Typography>
                      </Box>
                      <Button
                        variant='contained'
                        onClick={handleSaveAndClose}
                        size='small'
                        sx={{
                          backgroundColor: '#1A667A',
                          color: '#fff',
                          borderRadius: '6px',
                          minWidth: '180px',
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: '#15505c',
                          },
                        }}
                        startIcon={<SaveAltIcon />}>
                        Save and Close
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </DialogContent>

          {!isAwaitingRating &&
            activeCallState.status !== CallStatus.Ended &&
            activeCallState.status !== CallStatus.Ending && (
              <DialogActions
                sx={{
                  borderTop: '1px solid #CDCDCD',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  minHeight: '64px',
                  padding: '12px 25px',
                  flexShrink: 0,
                }}>
                {/* Call Control Bar */}
                <Box display='flex' justifyContent='space-between' width='100%' minHeight='30px'>
                  <Box display='flex' alignItems='center' gap={1}>
                    {isOnHold && (
                      <PauseIcon
                        sx={{
                          fontSize: '18px',
                          color: '#ff9800',
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.5 },
                          },
                        }}
                      />
                    )}
                    <Typography
                      variant='body2'
                      sx={{
                        color: isOnHold ? '#ff9800' : 'text.secondary',
                        fontWeight: isOnHold ? 600 : 400,
                      }}>
                      {activeCallState.call &&
                        activeCallState.direction === CallDirection.OUTBOUND &&
                        !isOnHold &&
                        'On Call'}
                      {activeCallState.call &&
                        activeCallState.direction === CallDirection.OUTBOUND &&
                        isOnHold &&
                        'On Hold'}
                    </Typography>
                  </Box>

                  {/* Call Duration Display */}
                  <Typography
                    variant='body2'
                    color='textSecondary'
                    sx={{ minWidth: 48, textAlign: 'center' }}>
                    {activeCallState.call &&
                      activeCallState.direction === CallDirection.OUTBOUND && (
                        <span>{formatCallDuration}</span>
                      )}
                  </Typography>
                </Box>

                <Box
                  display='flex'
                  borderRadius='32px'
                  border='1px solid #F5F5F5'
                  padding='10px'
                  width='60%'
                  justifyContent='space-between'>
                  {/* Mute/Unmute Button */}
                  <IconButton
                    disabled={
                      !activeCallState.call ||
                      activeCallState.direction !== CallDirection.OUTBOUND ||
                      !callLog
                    }
                    color={isMuted ? 'default' : 'success'}
                    onClick={handleToggleMute}
                    sx={{
                      background: isMuted ? '#eee' : '#4caf50',
                      color: '#fff',
                      '&:hover': { background: isMuted ? '#ddd' : '#388e3c' },
                      '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' },
                    }}>
                    {isMuted ? <MicOffIcon /> : <MicIcon />}
                  </IconButton>

                  {/* Hold Button */}
                  <IconButton
                    onClick={handleToggleHold}
                    disabled={
                      !activeCallState.call ||
                      activeCallState.direction !== CallDirection.OUTBOUND ||
                      !callLog
                    }
                    sx={{
                      color: isOnHold ? '#fff' : '#607d8b',
                      background: isOnHold ? '#ff9800' : '#f5f5f5',
                      borderRadius: '50%',
                      '&:hover': {
                        background: isOnHold ? '#f57c00' : 'rgba(0, 0, 0, 0.04)',
                      },
                      '&:disabled': { color: '#e0e0e0', background: '#f5f5f5' },
                    }}>
                    <PauseIcon />
                  </IconButton>

                  {!activeCallState.call ? (
                    /* Start Call Button */
                    <IconButton
                      color='success'
                      onClick={handleStartCall}
                      disabled={isStartCallDisabled}
                      sx={{
                        background: '#4caf50',
                        color: '#fff',
                        '&:hover': { background: '#388e3c' },
                        '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' },
                      }}>
                      <Phone />
                    </IconButton>
                  ) : (
                    /* End Call Button */
                    <IconButton
                      color='error'
                      onClick={handleEndCall}
                      disabled={
                        !activeCallState.call ||
                        activeCallState.direction !== CallDirection.OUTBOUND
                      }
                      sx={{
                        background: '#f44336',
                        color: '#fff',
                        '&:hover': { background: '#b71c1c' },
                        '&:disabled': { background: '#e0e0e0', color: '#9e9e9e' },
                      }}>
                      <CallEndIcon />
                    </IconButton>
                  )}
                </Box>
              </DialogActions>
            )}
        </Box>

        <Box
          onClick={
            canInitiateTransfer
              ? isDirectoryOpen
                ? handleCloseDirectory
                : handleOpenDirectory
              : undefined
          }
          sx={{
            position: 'absolute',
            right: '-50px',
            top: '10px',
            borderTopRightRadius: '8px',
            borderBottomRightRadius: '8px',
            padding: '12px 8px',
            boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.15)',
            transition: 'all 0.3s ease',
            backgroundColor: 'white',
            cursor: canInitiateTransfer ? 'pointer' : 'not-allowed',
            opacity: canInitiateTransfer ? 1 : 0.5,
            '&:hover': canInitiateTransfer
              ? {
                  backgroundColor: '#15505c',

                  '& .directory-icon': {
                    color: 'white',
                  },
                }
              : {},
          }}>
          <IconButton
            onClick={
              canInitiateTransfer
                ? isDirectoryOpen
                  ? handleCloseDirectory
                  : handleOpenDirectory
                : undefined
            }
            disabled={!canInitiateTransfer}
            sx={{
              width: '32px',
              height: '32px',
              color: '#fff',
              padding: '4px',
              '&:disabled': {
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
            title={
              !canInitiateTransfer
                ? 'Transfer not available'
                : isDirectoryOpen
                ? 'Hide Directory'
                : 'Show Directory'
            }>
            <BookOutlined
              className='directory-icon'
              sx={{
                width: '29px',
                height: '29px',
                color: canInitiateTransfer ? '#1A667A' : '#9e9e9e',
                transition: 'color 0.2s',
              }}
            />
          </IconButton>
        </Box>

        {/* Directory Panel */}
        {isDirectoryOpen && agent && (
          <Directory
            agents={agentDirectory}
            currentAgent={agent}
            conference={conference}
            lastUpdatedAt={agentDirectoryLastUpdatedAt}
            onWarmTransfer={handleWarmTransfer}
            onBlindTransfer={handleBlindTransfer}
            onRefresh={refreshAgentDirectory}
            isLoading={false}
          />
        )}
      </Dialog>
    </>
  );
};
